import { messaging } from "@/firebase/firebaseConfig";
import { detectBrowser } from "@/utils/helpers";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

function useServiceWorkers() {
  const [isInAppBrowserFirstTime, setisInAppBrowserFirstTime] = useState(false);
  const { t } = useTranslation();
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const registerDeviceUrl = `${BASE_URL}/api/dashboard/fcm`;
  const userToken = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );


  useEffect(() => {
    if (!userToken) return;

    if (Capacitor.getPlatform() === "web") {
      // Web-specific logic
      if ("serviceWorker" in navigator && navigator.serviceWorker) {
        detectBrowser().then((browser) => {
          const { type, name } = browser;
          if (type === "in-app") {
            if (!isInAppBrowserFirstTime) {
              setisInAppBrowserFirstTime(true);
              return toast.error(
                t("notificationContext.notification_unsupported", {
                  browser:
                    type === "in-app"
                      ? name || t("notificationContext.this")
                      : t("notificationContext.this"),
                }),
                { duration: 4000 },
              );
            } else {
              return;
            }
          } else {
            navigator.serviceWorker
              .register("/firebase-messaging-sw.js", { scope: "/" })
              .then((registration) => {
                const sendMessage = () => {
                  registration.active?.postMessage({
                    type: "config",
                    data: {
                      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
                      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
                      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
                      storageBucket: import.meta.env
                        .VITE_FIREBASE_STORAGE_BUCKET,
                      messagingSenderId: import.meta.env
                        .VITE_FIREBASE_MESSAGING_SENDER_ID,
                      appId: import.meta.env.VITE_FIREBASE_APP_ID,
                      measurementId: import.meta.env
                        .VITE_FIREBASE_MEASUREMENT_ID,
                    },
                  });
                };

                if (registration.active) {
                  sendMessage();
                } else {
                  navigator.serviceWorker.ready.then(() => {
                    sendMessage();
                  });
                }
              })
              .catch((err) => {
                console.error("Service Worker registration failed:", err);
              });

            // Request permission and generate the device token
            messaging
              ?.getToken({ vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY })
              .then((token) => {
                if (token) {
                  axios
                    .post(
                      registerDeviceUrl,
                      { token ,type : Capacitor.getPlatform()},
                      {
                        headers: {
                          Authorization: userToken,
                          "Content-Type": "application/json",
                        },
                      },
                    )

                    .catch((error) => {
                      console.error("Error registering device:", error);
                    });
                }
              })
              .catch((error) => {
                // Suppress error if permission is denied
                if (error.code === "messaging/permission-blocked") {
                  console.warn("Notifications are blocked by the user.");
                  return;
                }
                console.error("Error getting FCM token:", error);
              });
          }
        });
      }
    } else {
      // Android/iOS-specific logic

      PushNotifications.requestPermissions().then((result) => {
        if (result.receive === "granted") {
          PushNotifications.register();
        }
      });

      PushNotifications.addListener("registration", (token) => {
        axios
          .post(
            registerDeviceUrl,
            { token: token.value ,type: Capacitor.getPlatform()},
            {
              headers: {
                Authorization: userToken,
                "Content-Type": "application/json",
              },
            },
          )

          .catch((error) => {
            console.error("Error registering device:", error);
          });
      });
    }
  }, [isInAppBrowserFirstTime, registerDeviceUrl, t, userToken]);
}

export default useServiceWorkers;
